import { useEffect, useCallback, useRef } from 'react';
import { Flex } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { VideoPlayerControl } from '@/components/shared/interviews-revamp/interview-transcript/VideoPlayerControl';
import { useLazyGetInterviewTranscriptQuery } from '@/store/query/interviews';
import {
  selectCurrentTime,
  selectIsPIPMode,
  selectIsVideoModalOpen,
} from '@/store/interview-video/selectors';
import {
  updateTimeToJump,
  setToPlay,
  setVideoModalOpen,
} from '@/store/interview-video';
import '@/components/shared/interviews-revamp/interview-transcript/index.css';
import { KModal } from '@/ui-library/components/molecules/KModal/KModal';
import { type InterviewType } from '@/types/interview';
import { ModalVideoPlayer } from '@/components/shared/interviews-revamp/interview-transcript/ModalVideoPlayer';
import { LegacyModalVideoPlayer } from '@/components/shared/interviews-revamp/interview-transcript/LegacyModalVideoPlayer';
import { InterviewTranscriptPanel } from '@/components/shared/interviews-revamp/interview-transcript/InterviewTranscriptPanel';
import { useKFlags } from '@/hooks/use-k-flags';
import { canDownloadRecording } from '@/components/shared/interviews-revamp/interview-transcript/canDownloadRecording';

interface InterviewTranscriptProps {
  interview: InterviewType;
  interviewId?: string | undefined | number;
}

export const InterviewTranscript = ({
  interview,
  interviewId: interviewIdProps,
}: InterviewTranscriptProps) => {
  const dispatch = useDispatch();
  const flags = useKFlags();
  const canDownload = canDownloadRecording(interview);
  const currentTime = useSelector(selectCurrentTime);
  const isPIPMode = useSelector(selectIsPIPMode);
  const isVideoModalOpen = useSelector(selectIsVideoModalOpen);
  const { interviewId: interviewIdFromParams } = useParams<{
    interviewId: string;
  }>();
  const transcriptPanelRef = useRef<any>(null);
  const videoPlayerRef = useRef<HTMLDivElement>(null);
  const [
    getInterviewTranscript,
    { data, isFetching, isLoading, isUninitialized },
  ] = useLazyGetInterviewTranscriptQuery();

  const interviewId = interviewIdProps || interviewIdFromParams;

  const { recording_url } = interview || {};

  // Show loader on initial load before query is triggered or while fetching
  const showLoader = isUninitialized || isFetching || isLoading;

  useEffect(() => {
    if (interviewId) {
      getInterviewTranscript({ interviewId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewId]);

  const handleTimeJump = useCallback((timeInMs: number, toPlay = false) => {
    dispatch(updateTimeToJump(timeInMs));
    dispatch(setToPlay(toPlay));
    // Reset transcript manual scroll when user interacts via timestamp
    transcriptPanelRef.current?.resetManualScroll?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset transcript scroll when user interacts with video player controls
  const handleVideoPlayerControlInteraction = useCallback(() => {
    transcriptPanelRef.current?.resetManualScroll?.();
  }, []);

  // Reset transcript scroll when user touches video player controls
  useEffect(() => {
    const videoPlayer = videoPlayerRef.current;
    if (!videoPlayer) return;

    const handleVideoPlayerInteraction = (e: Event) => {
      // Check if click is on video player or its children
      const target = e.target as HTMLElement;
      if (videoPlayer.contains(target) || target === videoPlayer) {
        transcriptPanelRef.current?.resetManualScroll?.();
      }
    };

    // Use capture phase to catch all events including those in nested components
    videoPlayer.addEventListener('click', handleVideoPlayerInteraction, true);
    videoPlayer.addEventListener(
      'touchstart',
      handleVideoPlayerInteraction,
      true
    );

    // Also listen to pointer events for better touch support
    videoPlayer.addEventListener(
      'pointerdown',
      handleVideoPlayerInteraction,
      true
    );

    return () => {
      videoPlayer.removeEventListener(
        'click',
        handleVideoPlayerInteraction,
        true
      );
      videoPlayer.removeEventListener(
        'touchstart',
        handleVideoPlayerInteraction,
        true
      );
      videoPlayer.removeEventListener(
        'pointerdown',
        handleVideoPlayerInteraction,
        true
      );
    };
  }, []);

  return (
    <Flex
      flexDir="column"
      height="100%"
      overflow="hidden"
      position="relative"
      data-test-id="interview-transcript"
    >
      <Flex flex="1" minH="0" overflow="hidden">
        <InterviewTranscriptPanel
          ref={transcriptPanelRef}
          data={data}
          currentTime={currentTime}
          handleTimeJump={handleTimeJump}
          isLoading={showLoader}
        />
      </Flex>
      {recording_url && !isVideoModalOpen && (
        <Flex
          ref={videoPlayerRef}
          display={isPIPMode ? 'none' : 'flex'}
          minH="110px"
          borderTop="1px solid #EAEAEA"
        >
          <VideoPlayerControl
            videoUrl={recording_url}
            onMaximize={() => dispatch(setVideoModalOpen(true))}
            mode="mini"
            onPlayerInteraction={handleVideoPlayerControlInteraction}
          />
        </Flex>
      )}
      <KModal
        isOpen={isVideoModalOpen}
        onClose={() => dispatch(setVideoModalOpen(false))}
        hideCloseButton={true}
        size="6xl"
        title={null}
        footer={false}
        closeOnOverlayClick
        modalContentProps={{
          style: {
            height: '85vh',
            maxHeight: '85vh',
            width: '95vw',
            maxWidth: '95vw',
            position: 'fixed',
            border: 'none',
          },
        }}
        modalHeaderProps={{ padding: 0 }}
        modalBodyProps={{ maxH: '100%', padding: 0 }}
      >
        {/* Off falls back to the old player, which does not block the
            right-click download. */}
        {flags?.feNewVideoPlayer ? (
          <ModalVideoPlayer
            videoUrl={recording_url}
            transcriptData={data}
            currentTime={currentTime}
            handleTimeJump={handleTimeJump}
            interview={interview}
            isLoading={showLoader}
            canDownload={canDownload}
            onClose={() => dispatch(setVideoModalOpen(false))}
          />
        ) : (
          <LegacyModalVideoPlayer
            videoUrl={recording_url}
            transcriptData={data}
            currentTime={currentTime}
            handleTimeJump={handleTimeJump}
            interview={interview}
            isLoading={showLoader}
            onClose={() => dispatch(setVideoModalOpen(false))}
          />
        )}
      </KModal>
    </Flex>
  );
};
